'use client';

import React, { useState, useEffect } from 'react';
import { TrophyIcon } from '@heroicons/react/24/solid';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

interface Sponsor {
  id: string;
  attraction: string;
  sponsor_name: string;
  sponsor_sub: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  short_description: string;
  photo_url: string;
  sponsor_type: string;
  sort_order: number;
}

const colorPairs = [
  { color: 'from-primary/20 to-primary/5', borderColor: 'border-primary/30' },
  { color: 'from-accent/20 to-accent/5', borderColor: 'border-accent/30' },
  { color: 'from-secondary/10 to-secondary/5', borderColor: 'border-secondary/20' },
  { color: 'from-primary/15 to-primary/5', borderColor: 'border-primary/20' },
];

export default function ActivitySponsors() {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('sponsors')
          .select('*')
          .eq('sponsor_type', 'activity')
          .order('sort_order', { ascending: true });
        if (!error && data) setSponsors(data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchSponsors();
  }, []);

  if (loading) {
    return (
      <section>
        <div className="flex items-center gap-3 mb-4">
          <div className="gradient-primary rounded-xl p-2.5">
            <TrophyIcon className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">Activity Sponsors</h2>
            <p className="text-xs text-muted-foreground">Proudly supported by local businesses</p>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-2xl p-4 border border-border h-24 animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="gradient-primary rounded-xl p-2.5">
          <TrophyIcon className="w-5 h-5 text-secondary" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-foreground">Activity Sponsors</h2>
          <p className="text-xs text-muted-foreground">Proudly supported by local businesses</p>
        </div>
      </div>
      <div className="space-y-3">
        {sponsors?.map((s, i) => {
          const pair = colorPairs[i % colorPairs.length];
          return (
            <div
              key={s?.id}
              className={`bg-gradient-to-r ${pair.color} rounded-2xl p-4 card-shadow border ${pair.borderColor}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-card card-shadow flex items-center justify-center flex-shrink-0 border border-border overflow-hidden">
                  {s?.photo_url ? (
                    <Image src={s.photo_url} alt={`${s.sponsor_name} logo`} width={64} height={64} className="object-contain w-full h-full" />
                  ) : (
                    <span className="text-3xl">🏆</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Sponsoring</p>
                  <p className="font-bold text-sm text-foreground leading-tight">{s?.attraction}</p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className="text-[10px] text-muted-foreground font-medium">Sponsored by</span>
                  </div>
                  <p className="font-bold text-base text-foreground">{s?.sponsor_name}</p>
                  {s?.sponsor_sub && <p className="text-xs text-muted-foreground">{s.sponsor_sub}</p>}
                  {s?.short_description && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{s.short_description}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}